/**
 * Currency Formatting Utilities
 *
 * Centralized currency formatting for consistent display across the app.
 * Uses Intl.NumberFormat for proper locale-aware formatting.
 *
 * Amounts follow the INTERFACE language (`resolveLocale()`), like the plain
 * numbers next to them: ru "1 234,56 $", es "1234,56 US$", en "$1,234.56".
 * Intl picks the sign and its position for the locale (owner decision
 * 02.10, #1190). An explicitly passed `locale` still wins.
 */

import { resolveLocale } from './utils';

// Supported currencies (SSOT - matches backend)
export const SUPPORTED_CURRENCIES = ['USD'] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];
export const DEFAULT_CURRENCY: Currency = 'USD';

/**
 * Narrow an arbitrary currency code to one we can format.
 *
 * Stripe reports currencies lowercase (`"usd"`), and the formatters below take
 * the ISO uppercase form. Anything we don't support falls back to the default —
 * the alternative, rendering a foreign amount, is worse than rendering it in the
 * only currency the product actually prices in.
 */
export function toSupportedCurrency(code: string): Currency {
  const upper = code.toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(upper)
    ? (upper as Currency)
    : DEFAULT_CURRENCY;
}

/**
 * One `Intl.NumberFormat` per (locale, options).
 *
 * The `Intl` constructor is the expensive part (#359); `format()` is cheap.
 * Admin lists format a balance per organization per render, and the ledger
 * prints a rate on every row — hundreds of constructors a keystroke before this
 * cache (review #1161). Every formatter in this file goes through it. The key
 * carries the locale, so switching the interface language never hands back an
 * old formatter; option sets are a handful, so the map cannot grow.
 */
const numberFormatters = new Map<string, Intl.NumberFormat>();

export function numberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions = {}
): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

function currencyFormatter(
  locale: string,
  currency: Currency,
  fractionDigits: { min: number; max: number }
): Intl.NumberFormat {
  return numberFormatter(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits.min,
    maximumFractionDigits: fractionDigits.max,
  });
}

/**
 * The currency sign and the side of the number the locale puts it on, for
 * decorating an input field the way `formatCents` would write the amount:
 * en "$" before; ru "$", uk "USD" and es "US$" after (ICU's own symbols — the
 * sign is more than one character in uk/es, so callers size the room for it).
 */
export function currencySignPlacement(
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): { symbol: string; position: 'prefix' | 'suffix' } {
  const parts = numberFormatter(locale, { style: 'currency', currency }).formatToParts(1);
  const symbolIndex = parts.findIndex((part) => part.type === 'currency');
  const integerIndex = parts.findIndex((part) => part.type === 'integer');
  return {
    symbol: parts[symbolIndex]?.value ?? currency,
    position: symbolIndex > integerIndex ? 'suffix' : 'prefix',
  };
}

/**
 * Format cents to a currency string
 *
 * @param cents - Amount in cents (e.g., 1000 = $10.00)
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: the interface language's locale)
 * @returns Formatted string like "$10.00"
 *
 * @example
 * formatCents(1000) // "$10.00"
 * formatCents(1050) // "$10.50"
 * formatCents(0) // "$0.00"
 */
export function formatCents(
  cents: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return currencyFormatter(locale, currency, { min: 2, max: 2 }).format(cents / 100);
}

/**
 * Format a dollar amount (not cents) to a currency string
 *
 * @param amount - Amount in dollars (e.g., 10.50)
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: the interface language's locale)
 * @returns Formatted string like "$10.50"
 *
 * @example
 * formatDollars(10) // "$10.00"
 * formatDollars(10.5) // "$10.50"
 */
export function formatDollars(
  amount: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return currencyFormatter(locale, currency, { min: 2, max: 2 }).format(amount);
}

/**
 * Format a round amount without a trailing ",00" — top-up presets and range
 * bounds ("$10", ru "10 $"); a fractional amount still shows its cents.
 *
 * @example
 * formatWholeCents(1000) // "$10"
 * formatWholeCents(1050) // "$10.50"
 */
export function formatWholeCents(
  cents: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  const fraction = cents % 100 === 0 ? 0 : 2;
  return currencyFormatter(locale, currency, { min: fraction, max: fraction }).format(
    cents / 100
  );
}

/**
 * Ровно та сумма, которая спишется, — включая доли цента.
 *
 * `formatDollars` округляет до двух знаков, и там, где это подпись под кнопкой
 * «Начать», округление становится обещанием: ставки поддерживают четыре знака,
 * так что $0.014 подписывались как «$0.01», а $0.004 — как «$0.00», то есть
 * «бесплатно». Деньги хранятся и списываются с точностью до шести знаков
 * (`usage_charges`), и подпись обязана называть их, а не свою версию.
 *
 * Хвост не дорисовывается: целые центы выглядят как обычно («$10.14»), лишние
 * знаки появляются только когда они в сумме есть.
 */
export function formatExactDollars(
  amount: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return currencyFormatter(locale, currency, { min: 2, max: 6 }).format(amount);
}

/**
 * Format cents to a compact string (for display in tight spaces)
 *
 * @param cents - Amount in cents
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: the interface language's locale)
 * @returns Compact formatted string like "$1K" or "$1.5M"
 *
 * @example
 * formatCentsCompact(100000) // "$1K"
 * formatCentsCompact(1500000) // "$15K"
 * formatCentsCompact(100000000) // "$1M"
 */
export function formatCentsCompact(
  cents: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  const dollars = cents / 100;

  if (dollars >= 1_000) {
    // The suffix ("K", «тыс.», «mil») and its separator come from the locale.
    return numberFormatter(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: dollars >= 10_000 && dollars < 1_000_000 ? 0 : 1,
    }).format(dollars);
  }

  return formatCents(cents, currency, locale);
}

/**
 * Format a cost amount with high precision (5 decimal places)
 * Use for per-operation costs like ASR/LLM costs
 *
 * @param amount - Cost amount in dollars (e.g., 0.00123)
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: the interface language's locale)
 * @returns Formatted string like "$0.00123"
 *
 * @example
 * formatCost(0.00123) // "$0.00123"
 * formatCost(0.5) // "$0.50000"
 * formatCost(1.23456) // "$1.23456"
 */
export function formatCost(
  amount: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return currencyFormatter(locale, currency, { min: 5, max: 5 }).format(amount);
}

/**
 * Format a rate at the precision the ledger charges it in — "$0.1000".
 *
 * Neither neighbour fits a ledger row: `formatDollars` rounds to cents and
 * turns a $0.0050/chat rate into "$0.01" (a price we never charged), while
 * `formatRate` switches sub-dollar rates to cents ("5.5¢/min") and stops
 * matching the dollar amount printed beside it in the same row. Four decimals
 * is what the server writes into the stored description.
 *
 * The glyph comes from the currency code and never from the copy — see
 * `src/test/unit/i18n/money-copy.test.ts`, which fails any NEW string that
 * hard-codes `$` in front of a placeholder.
 *
 * @example
 * formatRateAmount(0.1) // "$0.1000"
 * formatRateAmount(0.005) // "$0.0050"
 */
export function formatRateAmount(
  amount: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return currencyFormatter(locale, currency, { min: 4, max: 4 }).format(amount);
}

/** English-language locale: the only one where "5.5¢" is how a price is written. */
const isEnglish = (locale: string): boolean => /^en(-|_|$)/i.test(locale);

/**
 * Format a per-unit rate (dollars per minute / per chat) for display.
 *
 * Published per-minute prices are all sub-$0.10 (e.g. 0.055), so a 2-decimal
 * dollar format would render 0.055 as "$0.06" — a wrong, customer-facing price.
 *
 * - English: sub-dollar rates in cents with up to two decimals —
 *   "5.5¢/min", "3.5¢/chat".
 * - Other languages: the cent glyph is not how a price is written there, so the
 *   rate stays in the currency format of the locale with 2 to 4 decimals —
 *   ru "0,055 $/min", es "0,055 US$/min" (0.055 is never rounded to 0,06).
 * - $1 and up, in every language: a 2-decimal dollar amount, "$1.50/min".
 *
 * Digits follow the interface language like the totals printed beside the
 * rate (#1190).
 *
 * @param dollarsPerUnit - Rate in dollars (e.g. 0.055)
 * @param unit - Unit label, e.g. 'min' or 'chat'
 * @param locale - Locale for formatting (default: the interface language's locale)
 *
 * @example
 * formatRate(0.055, 'min') // en: "5.5¢/min"; ru: "0,055 $/min"
 * formatRate(0.035, 'chat') // en: "3.5¢/chat"
 * formatRate(1.5, 'min') // "$1.50/min"
 */
export function formatRate(
  dollarsPerUnit: number,
  unit: string,
  locale: string = resolveLocale()
): string {
  if (dollarsPerUnit > 0 && dollarsPerUnit < 1) {
    if (!isEnglish(locale)) {
      const amount = currencyFormatter(locale, DEFAULT_CURRENCY, {
        min: 2,
        max: 4,
      }).format(dollarsPerUnit);
      return `${amount}/${unit}`;
    }
    const cents = numberFormatter(locale, { maximumFractionDigits: 2 }).format(
      Number((dollarsPerUnit * 100).toFixed(2))
    );
    return `${cents}¢/${unit}`;
  }
  return `${formatDollars(dollarsPerUnit, DEFAULT_CURRENCY, locale)}/${unit}`;
}

/**
 * Format cents to a high-precision cost string (5 decimal places)
 * Use for per-operation costs stored in cents
 *
 * @param cents - Amount in cents (e.g., 123 = $1.23000)
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: the interface language's locale)
 * @returns Formatted string like "$1.23000"
 *
 * @example
 * formatCostFromCents(123) // "$1.23000"
 * formatCostFromCents(1) // "$0.01000"
 */
export function formatCostFromCents(
  cents: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = resolveLocale()
): string {
  return formatCost(cents / 100, currency, locale);
}

/** Decimal and group separators of a locale, as `Intl` itself writes them. */
function numberSeparators(locale: string): { decimal: string; group: string } {
  const parts = numberFormatter(locale, { minimumFractionDigits: 1 }).formatToParts(
    12345.6
  );
  return {
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
  };
}

/**
 * Parse a currency string back to cents — the inverse of `formatCents`.
 *
 * Separators come from the locale: in ru/uk/es the comma is the decimal mark
 * ("1 234,56 $"), in en it is the group ("$1,234.56"). Reading every comma as
 * noise turned ru "1 234,56 $" into 123456 dollars, i.e. ×100.
 *
 * @param value - Currency string like "$10.50", "10,50 $" or "10.50"
 * @param locale - Locale the string is written in (default: the interface language's locale)
 * @returns Amount in cents (e.g., 1050) or null if invalid
 *
 * @example
 * parseToCents("$10.50", "en-US") // 1050
 * parseToCents("10,50 $", "ru-RU") // 1050
 * parseToCents("invalid") // null
 */
export function parseToCents(
  value: string,
  locale: string = resolveLocale()
): number | null {
  const { decimal, group } = numberSeparators(locale);

  // Whitespace covers NBSP / narrow NBSP, which ICU uses as the group in ru/uk.
  let cleaned = value.replace(/−/g, '-').replace(/\s/g, '');
  cleaned = cleaned.split(group).join('');
  cleaned = cleaned.split(decimal).join('.');
  // Currency symbols ("$", "US$") and anything else that is not part of the number
  cleaned = cleaned.replace(/[^0-9.-]/g, '');

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return null;
  }

  // Round to avoid floating point issues
  return Math.round(parsed * 100);
}

/** Integer digits of `head`, where the locale's group mark may split it — only in 3s. */
function integerDigits(head: string, group: string): string | null {
  if (/^\d+$/.test(head)) return head;
  if ((group !== '.' && group !== ',') || !head.includes(group)) return null;
  const [first = '', ...rest] = head.split(group);
  if (!/^[1-9]\d{0,2}$/.test(first) || !rest.every((part) => /^\d{3}$/.test(part))) {
    return null;
  }
  return head.split(group).join('');
}

/**
 * Read an amount a person TYPED into a money field, as integer cents — or null
 * when it cannot be read with certainty. The inverse of how the interface
 * language writes amounts, but tolerant of the other separator, because people
 * type "10.50" in ru and "5,5" in en.
 *
 * Tolerance never guesses a different amount:
 * - spaces (incl. NBSP / narrow NBSP) and "$", "US$", "USD" are ignored;
 * - the language's group mark counts as one only when every group after it has
 *   exactly three digits: en "5,000" is 5000, es "10.000" and ru "10 000" are
 *   10000;
 * - the last "." or "," is the decimal mark when 1–2 digits follow it and it
 *   occurs once: en "5,5" is 5.50, es/ru "10.50" is 10.50, ru "10,50" is 10.50;
 * - everything else — mixed or repeated marks ("1,00,0"), more than two decimal
 *   digits (ru "10,000" could be ten or ten thousand) — is null, never a best
 *   guess. Reading en "5,000" as 5.00 once charged $5 for $5,000.
 *
 * No float arithmetic: the cents are assembled from the digits as typed.
 *
 * @example
 * parseTypedAmountToCents("5,000", "en-US") // 500000
 * parseTypedAmountToCents("10 000", "ru-RU") // 1000000
 * parseTypedAmountToCents("10.50", "es-ES") // 1050
 * parseTypedAmountToCents("1,00,0", "en-US") // null
 */
export function parseTypedAmountToCents(
  input: string,
  locale: string = resolveLocale()
): number | null {
  const { group } = numberSeparators(locale);
  // The sign only as a prefix or a suffix: "10$00" is not $1,000.
  const spaced = input
    .trim()
    .replace(/^(USD|US\$|\$)\s*/i, '')
    .replace(/\s*(USD|US\$|\$)$/i, '')
    .split(/\s+/);
  // A space is a group mark too (ru/uk write "10 000"), held to the same rule:
  // groups of three, or ru "5 00" would be charged as 500.
  if (
    spaced.length > 1 &&
    (!/^[1-9]\d{0,2}$/.test(spaced[0] ?? '') ||
      !spaced.slice(1, -1).every((part) => /^\d{3}$/.test(part)) ||
      !/^\d{3}([.,]\d{1,2})?$/.test(spaced[spaced.length - 1] ?? ''))
  ) {
    return null;
  }
  const text = spaced.join('');
  if (!/^[\d.,]+$/.test(text) || !/\d/.test(text)) return null;

  const lastMark = Math.max(text.lastIndexOf('.'), text.lastIndexOf(','));
  let integer: string | null;
  let fraction = '';
  if (lastMark === -1) {
    integer = text;
  } else {
    const mark = text.charAt(lastMark);
    const head = text.slice(0, lastMark);
    const tail = text.slice(lastMark + 1);
    const markCount = text.split(mark).length - 1;
    if (tail.length === 3 && mark === group) {
      integer = integerDigits(text, group);
    } else if (tail.length <= 2 && markCount === 1) {
      if (tail === '' && head === '') return null;
      integer = head === '' ? '0' : integerDigits(head, group);
      fraction = tail;
    } else {
      return null;
    }
  }
  if (integer === null || integer.length > 12) return null;
  return Number(integer) * 100 + Number(fraction.padEnd(2, '0'));
}
